import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../config";
import AdminShell, { eur, d8, dt } from "./AdminShell";

const API = config.API_BASE_URL;
const PRODUCT_LABELS = { estiasync_buzz: "Buzz", ordering: "Ordering" };
const STATUSES = ["trial", "active", "grace", "inactive"];

function ProductChips({ c, t }) {
  return (
    <>
      {c.corePlan && <span className="ad-chip active">EstiaSync · {c.corePlan.plan}</span>}
      {c.products.map((p) => (
        <span key={p.product_code} className={`ad-chip ${p.status}`}>
          {PRODUCT_LABELS[p.product_code] || p.product_code} · {t(p.status)}
        </span>
      ))}
      {!c.corePlan && c.products.length === 0 && <span className="ad-chip">—</span>}
    </>
  );
}

// Vendor-side επεξεργασία στοιχείων επιχείρησης (P0: τα ΑΦΜ/emails ήταν placeholder
// γιατί δεν υπήρχε πουθενά UI). Ο πελάτης έχει τη δική του κάρτα στο Settings.
function ProfileEditor({ company, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    company: company.company || "",
    taxid: company.taxid || "",
    email: company.email || "",
    phone: company.phone || "",
    address: company.address || "",
  });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    setNote(null);
    try {
      await axios.patch(`${API}/admin/companies/${company.id}`, form);
      setNote({ ok: true, text: t("Saved.") });
      onSaved?.();
    } catch (e) {
      const code = e?.response?.data?.error;
      setNote({
        ok: false,
        text:
          code === "bad-taxid" ? t("Tax ID must be exactly 9 digits.")
          : code === "taxid-in-use" ? t("This Tax ID is already in use.")
          : code === "bad-email" ? t("Please enter a valid email address.")
          : t("Something went wrong — please try again."),
      });
    } finally {
      setBusy(false);
    }
  };

  const fields = [
    ["company", t("Legal name")],
    ["taxid", t("Tax ID")],
    ["email", t("Billing email")],
    ["phone", t("Phone")],
    ["address", t("Address")],
  ];
  return (
    <div style={{ marginBottom: 8 }}>
      {note && <div className={`ad-banner ${note.ok ? "" : "err"}`}>{note.text}</div>}
      {fields.map(([k, label]) => (
        <div className="ad-form-row" key={k}>
          <label style={{ minWidth: 110 }}>{label}</label>
          <input type="text" autoCapitalize="off" value={form[k]} onChange={set(k)} />
        </div>
      ))}
      <button className="ad-btn" disabled={busy} onClick={save}>💾 {t("Save")}</button>
    </div>
  );
}

// Ανάθεση βασικού πλάνου: dropdown πλάνων + διάρκεια σε μήνες + κουμπί.
function PlanAssign({ plans, busy, onAssign, t }) {
  const [planId, setPlanId] = useState("");
  const [months, setMonths] = useState(12);
  const selected = (plans || []).find((p) => String(p.id) === String(planId));
  return (
    <div className="ad-form-row">
      <select value={planId} disabled={busy} onChange={(e) => setPlanId(e.target.value)}>
        <option value="">{t("Choose plan")}…</option>
        {(plans || []).map((p) => <option key={p.id} value={p.id}>{p.plan}</option>)}
      </select>
      <select value={months} disabled={busy} onChange={(e) => setMonths(Number(e.target.value))}>
        {[1, 3, 6, 12, 24].map((m) => <option key={m} value={m}>{m} {t("months")}</option>)}
      </select>
      <button className="ad-btn primary" disabled={busy || !selected}
        onClick={() => onAssign(Number(planId), months, selected?.plan)}>
        ✅ {t("Assign plan")}
      </button>
    </div>
  );
}

function CompanyDetail({ id, onChanged }) {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/companies/${id}`).then((r) => setD(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="ad-detail-inner"><div className="ad-hint">…</div></div>;

  const changeStatus = async (code, status) => {
    const label = PRODUCT_LABELS[code] || code;
    if (!window.confirm(t("Change {{product}} status to \"{{status}}\" for this customer?", { product: label, status: t(status) }))) return;
    setBusy(true);
    setError(null);
    try {
      await axios.patch(`${API}/admin/companies/${id}/products/${code}`, { status });
      load();
      onChanged?.();
    } catch (e) {
      setError(t("Something went wrong — please try again."));
    } finally {
      setBusy(false);
    }
  };

  const assignPlan = async (planId, months, planName) => {
    if (!window.confirm(t("Assign the \"{{plan}}\" plan for {{months}} months? Any current subscription will be ended.", { plan: planName, months }))) return;
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API}/admin/companies/${id}/subscription`, { planId, months });
      load();
      onChanged?.();
    } catch (e) {
      setError(t("Something went wrong — please try again."));
    } finally {
      setBusy(false);
    }
  };

  const endSubscription = async () => {
    if (!window.confirm(t("End the current subscription now? The customer loses access to the core app (standalone products are not affected)."))) return;
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API}/admin/companies/${id}/subscription/end`);
      load();
      onChanged?.();
    } catch (e) {
      setError(t("Something went wrong — please try again."));
    } finally {
      setBusy(false);
    }
  };

  const impersonate = async () => {
    if (!window.confirm(t("Open this customer's environment? You will see the app exactly as they do."))) return;
    try {
      const r = await axios.post(`${API}/admin/impersonate`, { companyId: id });
      localStorage.setItem("user", JSON.stringify(r.data));
      window.location.assign("/");
    } catch (e) {
      setError(t("Something went wrong — please try again."));
    }
  };

  return (
    <div className="ad-detail-inner">
      <div>
        <h4>{t("Company details")}</h4>
        <button className="ad-btn primary" style={{ marginBottom: 8 }} onClick={impersonate}>
          🔓 {t("Open customer environment")}
        </button>
        <br />
        <ProfileEditor company={d.company} onSaved={() => { load(); onChanged?.(); }} />
        <div className="ad-hint">{t("Created")}: {d8(d.company.created_at)}</div>
        <h4 style={{ marginTop: 10 }}>{t("Activity (30d)")}</h4>
        <div className="ad-hint">
          {t("Reservations")}: {d.activity.reservations30} · {t("Buzz calls")}: {d.activity.buzzCalls30} · {t("Orders")}: {d.activity.orders30}
        </div>
      </div>

      <div>
        <h4>{t("Products & services")}</h4>
        {error && <div className="ad-banner err">{error}</div>}
        {/* Πάντα ΚΑΙ τα δύο standalone προϊόντα — και χωρίς γραμμή (upsert στο backend),
            ώστε ο super-admin να ενεργοποιεί συνδυασμούς με ένα dropdown. */}
        {Object.keys(PRODUCT_LABELS).map((code) => {
          const p = d.products.find((x) => x.product_code === code);
          // Πλάνο με Luxury buzzer (Pro/Multisite): το απλό Buzz δεν έχει νόημα — κλειδωμένο.
          // Αν όμως είναι ήδη ενεργό (κληρονομιά), μένει ξεκλείδωτο για να απενεργοποιηθεί.
          const luxuryPlan = code === "estiasync_buzz" && !!d.activeSubscription?.includesLuxuryBuzz;
          const buzzOn = ["trial", "active", "grace"].includes(p?.status);
          const lockedByLuxury = luxuryPlan && !buzzOn;
          return (
            <div key={code} className="ad-form-row">
              <label>{PRODUCT_LABELS[code]}</label>
              <select
                value={p?.status || "inactive"}
                disabled={busy || lockedByLuxury}
                onChange={(e) => changeStatus(code, e.target.value)}
              >
                {STATUSES.map((s) => <option key={s} value={s}>{t(s)}</option>)}
              </select>
              <span className="ad-hint">
                {lockedByLuxury
                  ? t("The {{plan}} plan already includes the Luxury buzzer", { plan: d.activeSubscription.plan })
                  : <>
                      {p?.trial_ends_at ? `${t("Trial ends")}: ${d8(p.trial_ends_at)}` : ""}
                      {p?.card_last4 ? ` · ${t("Card")}: ****${p.card_last4}` : ""}
                      {!p ? t("not set up yet") : ""}
                      {luxuryPlan && buzzOn ? ` · ⚠ ${t("The {{plan}} plan already includes the Luxury buzzer", { plan: d.activeSubscription.plan })}` : ""}
                    </>}
              </span>
            </div>
          );
        })}

        <h4 style={{ marginTop: 10 }}>{t("Core plan")}</h4>
        <div className="ad-hint" style={{ marginBottom: 6 }}>
          {d.activeSubscription
            ? <>{d.activeSubscription.plan} · {t("Active until")} {d8(d.activeSubscription.ends)}
                <button className="ad-btn" style={{ marginLeft: 8 }} disabled={busy} onClick={endSubscription}>
                  ⏹ {t("End now")}
                </button></>
            : t("No active core plan")}
        </div>
        <PlanAssign plans={d.plans} busy={busy} onAssign={assignPlan} t={t} />

        <h4 style={{ marginTop: 10 }}>{t("Core subscriptions")}</h4>
        {d.subscriptions.length === 0 && <div className="ad-hint">—</div>}
        {d.subscriptions.map((s) => (
          <div key={s.id} className="ad-hint">
            {s.plan}: {d8(s.starts)} → {d8(s.ends)} · {eur(Number(s.price))} ({t("paid")}: {eur(Number(s.paid))})
          </div>
        ))}
      </div>

      <div>
        <h4>{t("Users")} ({d.users.length})</h4>
        {d.users.map((u) => (
          <div key={u.id} className="ad-hint">
            {u.name || u.username} {u.view_admin ? "· admin" : ""}{u.view_buzz ? " · buzz" : ""}
          </div>
        ))}
        <h4 style={{ marginTop: 10 }}>{t("Recent payments")}</h4>
        {d.payments.length === 0 && <div className="ad-hint">—</div>}
        {d.payments.map((p) => (
          <div key={p.id} className="ad-hint">
            {dt(p.created_at)} · {eur(p.amountEur)} · <span className={`ad-chip ${p.status === "paid" ? "ok" : ""}`}>{t(p.status)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Λογαριασμοί Buzz κλειδωμένοι από απόπειρες αλλαγής ΑΦΜ — ξεκλείδωμα με ορισμό
// του σωστού ΑΦΜ (κάνει και lookup μητρώου server-side).
function LockedAfmPanel({ t }) {
  const [locked, setLocked] = useState([]);
  const [afm, setAfm] = useState({}); // ανά company id
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/buzz/afm-locked`).then((r) => setLocked(r.data.locked)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const unlock = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/buzz/afm-unlock`, { company: id, afm: afm[id] || "" });
      load();
    } catch (e) {
      alert(e?.response?.data?.error || "error");
    } finally { setBusy(false); }
  };

  if (!locked.length) return null;
  return (
    <div className="ad-toolbar" style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: 12, display: "block" }}>
      <strong>🔒 {t("Accounts locked after failed VAT attempts")}</strong>
      {locked.map((c) => (
        <div key={c.id} style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
          <span>#{c.id} <strong>{c.company}</strong> ({c.email || "—"}) · {t("current")}: {c.taxid} · {dt(c.afm_locked_at)}</span>
          <input className="ad-btn" style={{ width: 130 }} placeholder={t("Correct VAT (AFM)")} inputMode="numeric" maxLength={9}
            value={afm[c.id] || ""} onChange={(e) => setAfm({ ...afm, [c.id]: e.target.value.replace(/\D/g, "").slice(0, 9) })} />
          <button className="ad-btn primary" disabled={busy || (afm[c.id] || "").length !== 9} onClick={() => unlock(c.id)}>
            {t("Unlock")}
          </button>
        </div>
      ))}
    </div>
  );
}

export default function AdminCompanies() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/companies`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <AdminShell><div className="ad-empty">…</div></AdminShell>;

  const lastActivity = (c) => {
    const ts = [c.lastReservationAt, c.lastBuzzCallAt, c.lastOrderAt].filter(Boolean).map((s) => new Date(s));
    return ts.length ? new Date(Math.max(...ts)) : null;
  };

  return (
    <AdminShell>
      <LockedAfmPanel t={t} />
      <div className="ad-table-scroll">
        <table className="ad-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>{t("Customer")}</th>
              <th>{t("Products & services")}</th>
              <th>{t("Users")}</th>
              <th>{t("Last activity")}</th>
              <th>{t("Created")}</th>
            </tr>
          </thead>
          <tbody>
            {data.companies.map((c) => (
              <React.Fragment key={c.id}>
                <tr className="ad-clickable" onClick={() => setOpen(open === c.id ? null : c.id)}>
                  <td>{c.id}</td>
                  <td><strong>{c.name}</strong><div className="ad-hint">{c.company || ""}</div></td>
                  <td><ProductChips c={c} t={t} /></td>
                  <td>{c.usersCount}</td>
                  <td>{lastActivity(c) ? dt(lastActivity(c)) : "—"}</td>
                  <td>{d8(c.created_at)}</td>
                </tr>
                {open === c.id && (
                  <tr className="ad-detail">
                    <td colSpan={6}>
                      <CompanyDetail id={c.id} onChanged={load} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </AdminShell>
  );
}
